import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../App";

/**
 * useWebPush - Hook til Web Push Notifications
 * Abonnerer når bruger logger ind, unsubscriber ved logout
 */
export function useWebPush(token, isLoggedIn) {
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const registrationRef = useRef(null);
  const subscriptionRef = useRef(null);

  // Check browser support
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setPushSupported(supported);
    if (supported && typeof Notification !== 'undefined') {
      setPushPermission(Notification.permission);
    }
  }, []);

  // Register service worker
  const registerSW = useCallback(async () => {
    if (!pushSupported) return null;
    try {
      const reg = await navigator.serviceWorker.register('/sw-push.js');
      registrationRef.current = reg;
      return reg;
    } catch (err) {
      console.error('SW registration fejl:', err);
      return null;
    }
  }, [pushSupported]);

  // Subscribe to push
  const subscribePush = useCallback(async () => {
    if (!pushSupported || !token) return false;
    
    try {
      // Get VAPID key
      const keyRes = await axios.get(`${API}/push/vapid-public-key`);
      const vapidKey = keyRes.data.publicKey;
      if (!vapidKey) {
        console.warn('Ingen VAPID public key');
        return false;
      }

      // Register SW if not done
      let reg = registrationRef.current;
      if (!reg) {
        reg = await registerSW();
      }
      if (!reg) return false;

      // Wait for SW to be ready
      await navigator.serviceWorker.ready;

      // Request permission
      if (typeof Notification === 'undefined') return false;
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      if (permission !== 'granted') return false;

      // Convert VAPID key
      const applicationServerKey = urlBase64ToUint8Array(vapidKey);

      // Subscribe
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });

      subscriptionRef.current = subscription;

      // Send to backend
      await axios.post(`${API}/push/subscribe`, 
        { subscription: subscription.toJSON() },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      setPushSubscribed(true);
      return true;
    } catch (err) {
      console.error('Push subscribe fejl:', err);
      return false;
    }
  }, [pushSupported, token, registerSW]);

  // Unsubscribe from push
  const unsubscribePush = useCallback(async () => {
    try {
      const sub = subscriptionRef.current;
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        
        if (token) {
          await axios.post(`${API}/push/unsubscribe`, 
            { endpoint },
            { headers: { Authorization: `Bearer ${token}` }}
          ).catch(() => {});
        }
      }
      
      subscriptionRef.current = null;
      setPushSubscribed(false);
    } catch (err) {
      console.error('Push unsubscribe fejl:', err);
    }
  }, [token]);

  // Auto-subscribe when logged in
  useEffect(() => {
    if (isLoggedIn && token && pushSupported && !pushSubscribed) {
      subscribePush();
    }
  }, [isLoggedIn, token, pushSupported, pushSubscribed, subscribePush]);

  return {
    pushSupported,
    pushSubscribed,
    pushPermission,
    subscribePush,
    unsubscribePush,
  };
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default useWebPush;
